import { useNavigate } from 'react-router-dom';
import { motion, useReducedMotion, type Variants } from 'framer-motion';
import { ArrowRight, ChevronDown } from 'lucide-react';
import Button from '../ui/Button';
import NetworkCanvas from '../ui/NetworkCanvas';
import Marquee from '../ui/Marquee';
import { companyInfo } from '../../data/companyInfo';
import { EASE } from '../../utils/animations';

const partners = ['HP', 'Dell', 'Lenovo', 'Supermicro', 'Cisco', 'Arista', 'Juniper'];

const container: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12, delayChildren: 0.1 } },
};

const item: Variants = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: EASE } },
};

const HeroSection = () => {
  const reduce = useReducedMotion();
  const navigate = useNavigate();

  const [firstLine, secondLine] = [
    companyInfo.tagline.split(' ').slice(0, 2).join(' '),
    companyInfo.tagline.split(' ').slice(2).join(' '),
  ];

  return (
    <section id="home" className="relative isolate flex min-h-screen flex-col overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 -z-30 bg-gradient-to-br from-slate-950 via-brand-950 to-slate-900" />
      <NetworkCanvas className="pointer-events-none absolute inset-0 -z-20 h-full w-full" />
      <div className="absolute -left-32 top-12 -z-10 h-96 w-96 rounded-full bg-brand-500/20 blur-[120px] animate-aurora-drift" />
      <div className="absolute -right-24 bottom-32 -z-10 h-[30rem] w-[30rem] rounded-full bg-accent-500/20 blur-[130px] animate-float" />
      <div
        className="absolute inset-0 -z-10 bg-noise opacity-[0.12] mix-blend-soft-light"
        aria-hidden="true"
      />
      <div
        className="absolute inset-x-0 bottom-0 -z-10 h-44 bg-gradient-to-t from-slate-950 to-transparent"
        aria-hidden="true"
      />

      <div className="flex flex-1 items-center">
        <motion.div
          className="container-tight py-28 text-center"
          variants={container}
          initial={reduce ? false : 'hidden'}
          animate="visible"
        >
          <motion.span
            variants={item}
            className="inline-flex items-center gap-2.5 rounded-full border border-white/15 bg-white/5 px-4 py-1.5 text-sm font-medium text-slate-200 backdrop-blur"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400" />
            </span>
            Authorized Enterprise Technology Partner
          </motion.span>

          <motion.h1
            variants={item}
            className="mx-auto mt-8 max-w-5xl text-5xl font-bold leading-[1.02] tracking-tight text-white sm:text-7xl md:text-8xl"
          >
            {firstLine}
            <span className="mt-2 block bg-gradient-to-r from-brand-300 via-accent-400 to-brand-300 bg-[length:200%_auto] bg-clip-text text-transparent animate-gradient-x">
              {secondLine}
            </span>
          </motion.h1>

          <motion.p
            variants={item}
            className="mx-auto mt-7 max-w-2xl text-lg leading-relaxed text-slate-300 md:text-xl"
          >
            {companyInfo.description}
          </motion.p>

          <motion.div
            variants={item}
            className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row"
          >
            <Button variant="primary" size="lg" onClick={() => navigate('/contact')}>
              Schedule Consultation
              <ArrowRight className="h-5 w-5" aria-hidden="true" />
            </Button>
            <Button variant="secondary" size="lg" onClick={() => navigate('/products')}>
              Explore Solutions
            </Button>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <div className="relative z-10 flex justify-center pb-3" aria-hidden="true">
        <ChevronDown className="h-6 w-6 animate-bounce text-slate-500" />
      </div>

      {/* Partner marquee */}
      <div className="relative z-10 border-t border-white/10 bg-white/[0.03] py-6 backdrop-blur-sm">
        <p className="mb-4 text-center text-xs font-semibold uppercase tracking-widest text-slate-500">
          Powered by industry leaders
        </p>
        <Marquee items={partners} fadeFrom="from-slate-950" />
      </div>
    </section>
  );
};

export default HeroSection;
